<template>
  <SelectBase
    :downshift-props="{
      disabled: disabled || readonly,
      items: [],
      ...downshiftProps,
    }"
    :skip-option-bs="true"
    :popover-props="{
      ...popoverProps,
      statelessBs: {
        pt: 0
      },
    }"
    :show-action="true"
  >
    <template
      #select="{
        toggleOpen, isOpen,
      }"
    >
      <SelectButton
        input-mode="select"
        :value="valueText"
        :bs="bs"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="readonly"
        v-bind="$attrs"
        :is-open="isOpen"
        @click="() => toggleOpen()"
      />
    </template>

    <template #options>
      <CascadeTree
        :bs="{
          borderTopWidth: 0,
          borderRightWidth: 0,
          borderLeftWidth: 0,
          minWidth: 'unset',
        }"
        :ident-key="identKey"
        :text-key="textKey"
        :get-tree="getTree"
        :level-nums="levelNums"
        :value="localValue"
        :on-select-item="onSelect"
        v-bind="treeProps"
        @reach-end="onReachEnd"
      />
    </template>

    <template
      #action="{ toggleOpen }"
    >
      <NewBsBox :bs="{ mr: 12 }">
        <BeerButton
          :bs="{ mr: 0, minWidth: 'unset'}"
          modifier="body"
          variant="text"
          size="s"
          @click="clearSelect"
        >
          清空
        </BeerButton>
        <BeerButton
          :disabled="isDisable"
          :bs="{ mr: 0, minWidth: 'unset'}"
          modifier="brand"
          variant="text"
          size="s"
          @click="e => confirm(toggleOpen)"
        >
          确定
        </BeerButton>

      </NewBsBox>
    </template>

  </SelectBase>
</template>

<script>
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import CascadeTree from './CascadeTree.vue'
  import SelectButton from './SelectButton.vue'
  import SelectBase from './SelectBase.vue'

  import initStyle from './cascadeStyle'

  initStyle()

  /**
   * 级联选择控件
   *
   * 其中的选项是 @xhs/yam-beer/cmp/overlayers/Option， 可以传递对应的 props
   * - 如 { name: '选项A', value: 'A', binds: { disabled: true }}
   *
   */
  export default {
    name: 'CascadeSelect',
    components: {
      SelectBase,
      SelectButton,
      NewBsBox,
      BeerButton,
      CascadeTree,
    },
    props: {
      modelValue: {
        type: Array,
      },
      /**
       * 是否只读
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 透传其他给 CascadeTree 的参数
       *
       * @see #sfc-CascadeTree
       */
      treeProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 获取对应级别的选项 Promise 接口
       * @param {object} conf
       *        {object} conf.level 需要获取的 level 数， level 从 1 开始
       *        {object} conf.context 该 level 下选中 parent 的选项
       * @returns {promise}
       *          {object[]} promise.resolve 返回的选项列表，给 textKey, identKey, isLeaf 使用
       *          {error} promise.reject 出错的时候，会显示选项获取失败
       *
       * @see #sfc-CascadeTree
       */
      getTree: {
        type: Function,
        required: true,
      },
      /**
       * 选项 unique id key
       *
       * @see #sfc-CascadeTree
       */
      identKey: {
        type: String,
        required: true,
      },
      /**
       * 选项层级数量
       *
       * @see #sfc-CascadeTree
       */
      levelNums: {
        type: Number,
        default: 2,
      },
      /**
       * 选项 SelectButton 的 inline 样式
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 透传给 downshift 的参数
       */
      downshiftProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 透传给 popover 参数
       */
      popoverProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * placeholder
       */
      placeholder: {
        type: String,
        default: '请输入',
      },
      /**
       * 是否选择节点仅为叶子节点有效
       */
      leafValueOnly: {
        type: Boolean,
        default: true,
      },
      /**
       * 选项的 display name
       *
       * @see #sfc-CascadeTree
       */
      textKey: {
        type: String,
        default: 'name',
      },
      getBtnText: {
        type: Function,
      },
      /**
       * 是否选择到底了就直接设置 modelValue
       */
      endToSetVal: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['update:modelValue'],
    data() {
      return {
        localValue: this.modelValue || [],
        reachLeaf: false,
      }
    },
    computed: {
      valueText() {
        if (this.getBtnText) {
          return this.getBtnText(this.value)
        }

        if (this.modelValue) {
          return this.modelValue.map(i => i[this.textKey]).join(' > ')
        }

        return ''
      },
      isDisable() {
        if (this.localValue.length < 1) {
          return true
        }
        if (!this.leafValueOnly) {
          return false
        }
        return !this.reachLeaf
      },
      isControlled() {
        if (this.$attrs?.['update:modelValue']) {
          return true
        }
        return false
      },
      value() {
        return this.isControlled ? this.modelValue : this.localValue
      },
    },
    watch: {
      modelValue(v) {
        if (v && v.length > 0) {
          this.localValue = [
            ...v,
          ]
        } else {
          this.localValue = []
        }
      },
    },
    methods: {
      confirm(toggleOpen) {
        this.setValue()
        toggleOpen({ close: true })
      },
      clearSelect() {
        this.localValue = []
        this.$emit('update:modelValue', this.localValue)
      },
      isDirty() {
        const localStr = (this.localValue || []).map(i => i[this.identKey]).join('-')
        const modelStr = (this.modelValue || []).map(i => i[this.identKey]).join('-')
        return localStr !== modelStr
      },
      onReachEnd(flag) {
        this.reachLeaf = flag
        if (this.reachLeaf && this.isDirty() && this.endToSetVal) {
          this.setValue()
        }
      },
      setValue() {
        if (!this.isDisable) {
          this.$emit('update:modelValue', this.localValue)
        }
      },
      onSelect(o, index) {
        const level = index + 1
        if (level === 1) {
          this.localValue = [o]
        } else {
          this.localValue = this.localValue.slice(0, level - 1)
            .concat([o])
        }
        this.setValue()
      },
    },
  }
</script>
